/**
 * @fileoverview AR-924 — One link of the delivery: glyph, clickable URL, "added by" meta and remove.
 */

import { ActionIcon, Anchor, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconLink, IconTrash } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { TaskTrackDeliveryLink } from '../../types'
import { formatDateTime } from './dateFormat'

type Translate = (key: string, options?: object) => string

const GLYPH_SIZE = 'lg'
const GLYPH_ICON_SIZE = 18
const TRASH_ICON_SIZE = 16
const NO_SPACING = 0

function submittedMeta(link: TaskTrackDeliveryLink, date: string, t: Translate): string {
  if (!link.addedBy) return t('task_tracker.teacher_upload.submitted_at', { date })
  if (link.addedBy.currentUser) return t('task_tracker.teacher_upload.submitted_at_added_by_you', { date })
  return t('task_tracker.teacher_upload.submitted_at_added_by', { date, teacher: link.addedBy.name })
}

interface DeliveryLinkCardProps {
  readonly link: TaskTrackDeliveryLink
  readonly readOnly: boolean
  readonly removing: boolean
  readonly onRemove: (linkId: string) => void
}

export function DeliveryLinkCard({ link, readOnly, removing, onRemove }: DeliveryLinkCardProps) {
  const { t, i18n } = useTranslation()
  const date = formatDateTime(link.createdAt, i18n.language)

  return (
    <Paper withBorder radius="md" px="sm" py="xs">
      <Group position="apart" noWrap align="center">
        <Group spacing="sm" noWrap sx={{ minWidth: 0 }}>
          <ThemeIcon variant="light" radius="xl" size={GLYPH_SIZE}>
            <IconLink size={GLYPH_ICON_SIZE} />
          </ThemeIcon>
          <Stack spacing={NO_SPACING} sx={{ minWidth: 0 }}>
            <Anchor href={link.url} target="_blank" rel="noopener noreferrer" size="sm" weight={500} lineClamp={1}>
              {link.url}
            </Anchor>
            <Text size="xs" color="dimmed">{submittedMeta(link, date, t)}</Text>
          </Stack>
        </Group>
        {!readOnly && (
          <ActionIcon
            variant="subtle"
            color="red"
            loading={removing}
            onClick={() => onRemove(link.id)}
            aria-label={t('task_tracker.teacher_upload.remove_link')}
          >
            <IconTrash size={TRASH_ICON_SIZE} />
          </ActionIcon>
        )}
      </Group>
    </Paper>
  )
}
